import axios from 'axios';
import { fetchWithOfflineSupport, getAllSubstancesOffline, getSubstanceByIdOffline } from '../../helpers/offlineApi';
import { setLoading, setLoadingSubstances, setMessage } from './status';

export const SET_SUBSTANCES = 'SET_SUBSTANCES';
export const SET_SUBSTANCE = 'SET_SUBSTANCE';
export const CLEAR_SUBSTANCES = 'CLEAR_SUBSTANCES';

const API_BASE_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';

export const getSubstancesWithFallback = async (options = {}) => {
    const { limit = 2000, useCache = true } = options;

    try {
        try {
            const response = await axios.get(`${API_BASE_URL}/substances`, {
                params: {
                    limit: limit,
                    all: true
                },
                timeout: 15000
            });

            let mongoSubstances = [];
            if (response.data && response.data.substances && response.data.substances.length > 0) {
                mongoSubstances = response.data.substances.map(substance => ({
                    id: substance._id || substance.id,
                    _id: substance._id || substance.id,
                    ...substance,
                    source: 'mongodb'
                }));
            } else if (response.data && Array.isArray(response.data)) {
                mongoSubstances = response.data.map(substance => ({
                    id: substance._id || substance.id,
                    _id: substance._id || substance.id,
                    ...substance,
                    source: 'mongodb'
                }));
            }

            if (mongoSubstances.length > 0) {
                return mongoSubstances;
            }
        } catch (mongoError) {}

        try {
            const response = await axios.get(`${API_BASE_URL}/substance`, {
                params: { limit: limit },
                timeout: 15000
            });

            if (response.data && Array.isArray(response.data)) {
                const mongoSubstances = response.data.map(substance => ({
                    id: substance._id || substance.id,
                    _id: substance._id || substance.id,
                    ...substance,
                    source: 'mongodb_alt'
                }));

                if (mongoSubstances.length > 0) {
                    return mongoSubstances;
                }
            }
        } catch (altError) {}

        return [];

    } catch (error) {
        return [];
    }
};

export const fetchSubstances = (filters = {}, preferOffline = false) => async dispatch => {
    dispatch(setLoadingSubstances(true));

    try {
        let substances = [];

        let primarySubstances = [];
        try {
            primarySubstances = await getSubstancesWithFallback({ limit: 2000 });
        } catch (error) {}

        let pouchSubstances = [];
        try {
            pouchSubstances = await getAllSubstancesOffline(filters);
        } catch (pouchError) {}

        let jsonSubstances = [];
        try {
            const classesData = await import('../../json/classe.json');
            const classes = classesData.default || classesData;

            if (Array.isArray(classes) && classes.length > 0) {
                jsonSubstances = classes.slice(0, 50).map((classe, index) => ({
                    _id: `json_${index + 1}`,
                    name: classe.nom_classe || classe.name || 'Classe pharmaceutique',
                    nom: classe.nom_classe || classe.name || 'Classe pharmaceutique',
                    DCI: classe.nom_classe || classe.name,
                    substanceContreIndiquees: '',
                    substanceContreIndiquees1: '',
                    source: 'json_classes'
                }));
            }
        } catch (jsonError) {}

        if (primarySubstances.length > 0) {
            substances = primarySubstances;
        } else if (pouchSubstances.length > 20) {
            substances = pouchSubstances;
        } else if (jsonSubstances.length > 0) {
            substances = jsonSubstances;
        }

        const normalizedSubstances = substances.map(substance => ({
            ...substance,
            name: substance.name || substance.nom || substance.DCI || substance.substance || 'Substance',
            _id: substance._id || substance.id,
            substanceContreIndiquees: substance.substanceContreIndiquees || '',
            substanceContreIndiquees1: substance.substanceContreIndiquees1 || substance.substanceContreIndiquees || ''
        }));

        dispatch({
            type: SET_SUBSTANCES,
            payload: normalizedSubstances
        });

        return normalizedSubstances;

    } catch (error) {

        dispatch(setMessage({
            message: 'Impossible de charger les substances',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoadingSubstances(false));
    }
};

export const fetchSubstanceById = (substanceId) => async dispatch => {
    if (!substanceId) {
        dispatch(setMessage({
            message: 'ID de substance requis',
            status: 'danger'
        }));
        return null;
    }

    dispatch(setLoading(true));

    try {
        const result = await fetchWithOfflineSupport(
            async () => {
                const response = await axios.get(`${API_BASE_URL}/substances/${substanceId}`, {
                    timeout: 8000
                });
                return response.data;
            },
            async () => {
                return await getSubstanceByIdOffline(substanceId);
            }
        );

        if (!result.data) {
            throw new Error('Substance non trouvée');
        }

        dispatch({
            type: SET_SUBSTANCE,
            payload: result.data
        });

        if (result.source === 'offline') {
            dispatch(setMessage({
                message: 'Substance chargée depuis le cache hors ligne',
                status: 'info'
            }));
        }

        return result.data;

    } catch (error) {

        dispatch(setMessage({
            message: 'Substance non trouvée',
            status: 'danger'
        }));
        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

export const searchSubstances = (searchCriteria) => async dispatch => {
    const { query, classe, exact = false } = searchCriteria;

    if (!query && !classe) {
        dispatch({
            type: SET_SUBSTANCES,
            payload: []
        });
        return [];
    }

    dispatch(setLoadingSubstances(true));

    try {
        const result = await fetchWithOfflineSupport(
            async () => {
                const response = await axios.get(`${API_BASE_URL}/substances/search`, {
                    params: {
                        q: query || '',
                        classe: classe || '',
                        exact: exact
                    },
                    timeout: 10000
                });
                return response.data.data || [];
            },
            async () => {
                const { getAllSubstancesOffline } = await import('../../helpers/offlineApi');
                if (query) {
                    return await getAllSubstancesOffline({
                        search: query,
                        exact,
                        limit: searchCriteria.limit || 100
                    });
                } else {
                    return await getAllSubstancesOffline({
                        classe,
                        limit: searchCriteria.limit || 100
                    });
                }
            }
        );

        dispatch({
            type: SET_SUBSTANCES,
            payload: result.data
        });

        if (result.source === 'offline') {
            dispatch(setMessage({
                message: `${result.data.length} substances trouvées hors ligne`,
                status: 'info'
            }));
        }

        return result.data;

    } catch (error) {

        dispatch(setMessage({
            message: 'Erreur lors de la recherche de substances',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoadingSubstances(false));
    }
};

export const analyzeInteractions = (substanceIds) => async dispatch => {
    if (!substanceIds || substanceIds.length < 2) {
        dispatch(setMessage({
            message: 'Au moins 2 substances sont requises pour l\'analyse',
            status: 'warning'
        }));
        return [];
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.post(`${API_BASE_URL}/substances/interactions`, {
            substances: substanceIds
        }, {
            timeout: 15000
        });

        const interactions = response.data.data || [];

        if (interactions.length === 0) {
            dispatch(setMessage({
                message: 'Aucune interaction détectée entre ces substances',
                status: 'success'
            }));
        } else {
            const severityCount = interactions.reduce((acc, interaction) => {
                acc[interaction.severity] = (acc[interaction.severity] || 0) + 1;
                return acc;
            }, {});

            const message = `${interactions.length} interaction(s) détectée(s): ${Object.entries(severityCount).map(([sev, count]) => `${count} ${sev}`).join(', ')}`;

            dispatch(setMessage({
                message,
                status: 'warning'
            }));
        }

        return interactions;

    } catch (error) {

        dispatch(setMessage({
            message: 'Impossible d\'analyser les interactions (fonctionnalité en ligne uniquement)',
            status: 'danger'
        }));
        return [];
    } finally {
        dispatch(setLoading(false));
    }
};

export const createSubstance = (substanceData) => async dispatch => {
    if (!substanceData.nom || !substanceData.classe) {
        dispatch(setMessage({
            message: 'Nom et classe de la substance sont requis',
            status: 'danger'
        }));
        return null;
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.post(`${API_BASE_URL}/substances`, substanceData, {
            timeout: 10000
        });

        const newSubstance = response.data;

        dispatch(setMessage({
            message: 'Substance créée avec succès',
            status: 'success'
        }));

        dispatch(fetchSubstances());

        return newSubstance;

    } catch (error) {

        const errorMessage = error.response?.data?.message || 'Erreur lors de la création de la substance';
        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));

        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

export const updateSubstance = (substanceId, updateData) => async dispatch => {
    if (!substanceId) {
        dispatch(setMessage({
            message: 'ID de substance requis',
            status: 'danger'
        }));
        return null;
    }

    dispatch(setLoading(true));

    try {
        const response = await axios.put(`${API_BASE_URL}/substances/${substanceId}`, updateData, {
            timeout: 10000
        });

        const updatedSubstance = response.data;

        dispatch({
            type: SET_SUBSTANCE,
            payload: updatedSubstance
        });

        dispatch(setMessage({
            message: 'Substance mise à jour avec succès',
            status: 'success'
        }));

        return updatedSubstance;

    } catch (error) {


        const errorMessage = error.response?.data?.message || 'Erreur lors de la mise à jour';
        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));

        return null;
    } finally {
        dispatch(setLoading(false));
    }
};

export const clearSubstances = () => ({
    type: CLEAR_SUBSTANCES
});

export const deleteSubstance = (substanceId) => async dispatch => {
    if (!substanceId) {
        dispatch(setMessage({
            message: 'ID de substance requis',
            status: 'danger'
        }));
        return false;
    }

    dispatch(setLoading(true));

    try {
        await axios.delete(`${API_BASE_URL}/substances/${substanceId}`, {
            timeout: 10000
        });

        dispatch(setMessage({
            message: 'Substance supprimée avec succès',
            status: 'success'
        }));

        dispatch(fetchSubstances());

        return true;

    } catch (error) {


        const errorMessage = error.response?.data?.message || 'Erreur lors de la suppression';
        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));

        return false;
    } finally {
        dispatch(setLoading(false));
    }
};

export const clearSelectedSubstance = () => ({
    type: SET_SUBSTANCE,
    payload: null
});

export const getSubstancesByName = (name, limit = 20) => async dispatch => {
    if (!name || name.trim().length < 2) {
        dispatch({
            type: SET_SUBSTANCES,
            payload: []
        });
        return [];
    }

    dispatch(setLoadingSubstances(true));

    try {
        try {
            const API_MONGO_URL = 'https://apiv1-32dxg5qwva-uc.a.run.app/apiV1/mongo';
            const response = await axios.get(`${API_MONGO_URL}/products`, {
                params: {
                    dci: name.trim(),
                    limit: 100
                },
                timeout: 10000
            });

            if (response.data && response.data.products && response.data.products.length > 0) {
                const dciSet = new Set();
                response.data.products.forEach(product => {
                    if (product.DCI) {
                        const dcis = product.DCI.split(',').map(dci => dci.trim());
                        dcis.forEach(dci => {
                            if (dci && dci.toLowerCase().includes(name.toLowerCase())) {
                                dciSet.add(dci);
                            }
                        });
                    }
                });

                const suggestions = Array.from(dciSet)
                    .slice(0, limit)
                    .map(dci => ({
                        _id: dci.toLowerCase().replace(/[^a-z0-9]/g, '_'),
                        name: dci,
                        DCI: dci,
                        source: 'mongodb'
                    }));

                dispatch({
                    type: SET_SUBSTANCES,
                    payload: suggestions
                });

                return suggestions;
            }
        } catch (mongoError) {}

        return dispatch(searchSubstances({ query: name, limit }));

    } catch (error) {

        dispatch({
            type: SET_SUBSTANCES,
            payload: []
        });
        return [];
    } finally {
        dispatch(setLoadingSubstances(false));
    }
};

export const setSubstances = (substances) => ({
    type: SET_SUBSTANCES,
    payload: substances
});

export const setSubstance = (substance) => ({
    type: SET_SUBSTANCE,
    payload: substance
});

export const getSubstance = (substanceId) => async dispatch => {
    return dispatch(fetchSubstanceById(substanceId));
};

export const newSubstance = (substanceData) => async dispatch => {
    return dispatch(createSubstance(substanceData));
};

export const getSubstanceById = (substanceId) => async dispatch => {
    return dispatch(fetchSubstanceById(substanceId));
};

export const editSubstance = (substanceId, updateData) => async dispatch => {
    return dispatch(updateSubstance(substanceId, updateData));
};

export default {
    fetchSubstances,
    fetchSubstanceById,
    getSubstanceById,
    searchSubstances,
    getSubstancesByName,
    setSubstances,
    setSubstance,
    getSubstance,
    newSubstance,
    analyzeInteractions,
    createSubstance,
    updateSubstance,
    editSubstance,
    deleteSubstance,
    clearSubstances,
    clearSelectedSubstance
};